<template>
  <div class="wl-route wl-form uranus-scrollbar" @pointerdown.stop>
    <section class="wl-route__card wl-route__takeoff">
      <div class="wl-route__takeoff-status">
        <template v-if="takeoff">
          <CheckCircleFilled class="ok" />
          <span>{{ draft.dock ? `已按机场“${draft.dock.name}”设置参考起飞点` : '已设置参考起飞点' }}</span>
        </template>
        <template v-else>
          <ExclamationCircleFilled class="warn" />
          <span>{{ draft.dock ? `机场“${draft.dock.name}”尚未上报位置` : '未设置参考起飞点' }}</span>
        </template>
      </div>
      <a-dropdown :trigger="['click']" placement="bottomRight" overlay-class-name="wl-menu" @visibleChange="visible => visible && editor.loadDocks()">
        <button type="button" class="wl-route__link"><EnvironmentOutlined /> {{ takeoff ? '重设起飞点' : '设置起飞点' }}</button>
        <template #overlay>
          <a-menu>
            <a-sub-menu key="dock" title="使用机场位置">
              <a-menu-item v-if="editor.loadingDocks.value && !editor.docks.value.length" disabled>正在读取机场…</a-menu-item>
              <a-menu-item v-else-if="!editor.docks.value.length" disabled>当前工作空间没有绑定机场</a-menu-item>
              <a-menu-item v-for="dock in editor.docks.value" :key="dock.sn" :disabled="!dock.position" @click="editor.useDock(dock.sn)">
                {{ dock.name }} · {{ dock.modelName }}{{ dock.position ? '' : '（未上报位置）' }}
              </a-menu-item>
            </a-sub-menu>
            <a-menu-item @click="editor.setTool('takeoff')">在地图上点选</a-menu-item>
            <a-menu-item v-if="editor.aircraft.active" @click="useAircraftPosition">使用虚拟飞行器所在地面位置</a-menu-item>
            <a-menu-item v-if="takeoff" danger @click="editor.clearTakeoff">清除起飞点</a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
      <p v-if="takeoff" class="wl-route__coords">{{ takeoff.latitude.toFixed(6) }}, {{ takeoff.longitude.toFixed(6) }}<template v-if="takeoff.ellipsoidHeight !== null">，椭球高 {{ takeoff.ellipsoidHeight.toFixed(1) }} m</template><template v-if="editor.padTerrain.value !== null">，地形 {{ editor.padTerrain.value.toFixed(1) }} m</template></p>
      <p v-if="editor.padHeightGap.value !== null && Math.abs(editor.padHeightGap.value) > 15" class="wl-route__gap">上报高度比地形{{ editor.padHeightGap.value > 0 ? '高' : '低' }} {{ Math.abs(editor.padHeightGap.value).toFixed(1) }} m。地图按地形显示起飞点；机场在屋顶时请填写离地高度，否则请检查机场定位。</p>
      <div v-if="takeoff" class="wl-route__agl">
        <a-tooltip title="起飞点相对其正下方地面的高度（WPML takeOffRefPointAGLHeight）。机场落在地面上时为 0；机场安装在屋顶上时填屋顶离地高度。司空 2 按该值把起飞点抬离地形。"><span>起飞点离地高度 <InfoCircleOutlined /></span></a-tooltip>
        <a-input-number size="small" :value="takeoff.aglHeight ?? 0" :min="-10" :max="500" :precision="2" :step="0.1" @change="setAglHeight" /><span class="unit">m</span>
      </div>
    </section>

    <template v-if="draft.templateType === 'waypoint'">
      <section class="wl-route__card">
        <h4>拍照设置</h4>
        <div class="wl-route__segmented">
          <button type="button" :class="{ active: photoMode === 'visible' }" @click="setPhotoMode('visible')">可见光</button>
          <button type="button" :class="{ active: photoMode === 'ir' }" :disabled="!hasIr" :title="hasIr ? '' : '当前负载没有红外镜头'" @click="setPhotoMode('ir')">红外照片</button>
          <button type="button" :class="{ active: photoMode === 'both' }" :disabled="!hasIr" @click="setPhotoMode('both')">可见光 + 红外</button>
        </div>
        <div v-if="(editor.droneProfile.value?.payloads.length || 0) > 1" class="wl-route__row">
          <span>负载</span>
          <a-select size="small" :value="draft.payloadModelKey" dropdown-class-name="wl-select" @change="setPayload">
            <a-select-option v-for="item in editor.droneProfile.value?.payloads || []" :key="item.key" :value="item.key">{{ item.name }}</a-select-option>
          </a-select>
        </div>
      </section>

      <section class="wl-route__card">
        <div class="wl-route__segmented">
          <button type="button" :class="{ active: draft.missionConfig.flyToWaylineMode === 'safely' }" @click="set(() => { draft.missionConfig.flyToWaylineMode = 'safely' })">垂直爬升</button>
          <button type="button" :class="{ active: draft.missionConfig.flyToWaylineMode === 'pointToPoint' }" @click="set(() => { draft.missionConfig.flyToWaylineMode = 'pointToPoint' })">倾斜爬升</button>
        </div>
        <div class="wl-route__figure">
          <svg viewBox="0 0 200 110" class="wl-route__diagram">
            <text x="60" y="12" fill="#c9ccd2" font-size="9">垂直爬升飞行轨迹</text>
            <text x="4" y="52" fill="#c9ccd2" font-size="9">倾斜爬升飞行轨迹</text>
            <path d="M20 26 L150 26" :stroke="draft.missionConfig.flyToWaylineMode === 'safely' ? '#ffb020' : '#5a5d64'" stroke-width="1.5" stroke-dasharray="4 3" fill="none" />
            <path d="M20 26 L150 70" :stroke="draft.missionConfig.flyToWaylineMode === 'pointToPoint' ? '#19be6b' : '#5a5d64'" stroke-width="1.5" stroke-dasharray="4 3" fill="none" />
            <path d="M14 20 L26 20 L20 32 Z" fill="#2d8cf0" />
            <path d="M150 26 L150 98" stroke="#19be6b" stroke-width="1.5" stroke-dasharray="3 3" fill="none" />
            <path d="M146 30 L150 26 L154 30 M146 94 L150 98 L154 94" stroke="#19be6b" stroke-width="1.5" fill="none" />
            <text x="156" y="60" fill="#c9ccd2" font-size="9">安全起飞高</text>
            <text x="164" y="71" fill="#c9ccd2" font-size="8">(ALT)</text>
            <circle cx="100" cy="88" r="7" fill="none" stroke="#e9eaec" stroke-width="1.5" /><path d="M100 81 L100 74 M100 95 L100 100" stroke="#e9eaec" stroke-width="1.5" />
            <rect x="142" y="98" width="16" height="4" fill="#e9eaec" />
            <path d="M0 102 L200 102" stroke="#5a5d64" />
          </svg>
          <div class="wl-route__stepper">
            <button type="button" @click="stepSafety(100)">+100</button>
            <button type="button" @click="stepSafety(10)">+10</button>
            <a-input-number size="small" :value="draft.missionConfig.takeOffSecurityHeight" :min="8" :max="1500" :precision="0" :controls="false" @change="setSafety" /><span class="unit">m</span>
            <button type="button" @click="stepSafety(-10)">-10</button>
            <button type="button" @click="stepSafety(-100)">-100</button>
          </div>
        </div>
        <p v-if="draft.dock" class="wl-route__hint">基于机场创建的航线默认安全起飞高 80 m，飞行器先爬升到该高度再飞向首航点。</p>
      </section>

      <section class="wl-route__card">
        <h4>航点高度模式</h4>
        <div class="wl-route__segmented">
          <button type="button" :class="{ active: heightMode === 'WGS84' }" @click="editor.setHeightMode('WGS84')">海拔高度</button>
          <button type="button" :class="{ active: heightMode === 'relativeToStartPoint' }" @click="editor.setHeightMode('relativeToStartPoint')">相对起飞点高度</button>
          <a-tooltip title="相对地形高度需要机载地形数据，当前版本暂不支持"><button type="button" disabled>相对地形高度</button></a-tooltip>
        </div>
        <div class="wl-route__figure">
          <svg viewBox="0 0 200 110" class="wl-route__diagram">
            <path d="M14 14 L26 14 L20 26 Z" fill="#2d8cf0" />
            <path d="M20 26 L20 90" stroke="#5a5d64" stroke-dasharray="3 3" />
            <g transform="translate(112 22)"><path d="M-10 0 L10 0 M0 -6 L0 6" stroke="#e9eaec" stroke-width="2" /><circle r="3" fill="#e9eaec" /></g>
            <path d="M138 24 L138 96" stroke="#19be6b" stroke-width="1.5" stroke-dasharray="3 3" fill="none" />
            <path d="M134 28 L138 24 L142 28 M134 92 L138 96 L142 92" stroke="#19be6b" stroke-width="1.5" fill="none" />
            <text x="146" y="58" fill="#c9ccd2" font-size="9">{{ heightMode === 'WGS84' ? '海拔高度' : '相对高度' }}</text>
            <text x="150" y="69" fill="#c9ccd2" font-size="8">{{ heightMode === 'WGS84' ? '(ASL)' : '(ALT)' }}</text>
            <circle cx="112" cy="70" r="6" fill="none" stroke="#e9eaec" stroke-width="1.5" /><path d="M112 76 L112 84" stroke="#e9eaec" stroke-width="1.5" />
            <path d="M100 84 L124 84" stroke="#e9eaec" stroke-width="2" />
            <path d="M0 84 Q30 70 60 84 T120 84 T200 84" stroke="#ffb020" stroke-width="1.2" fill="none" /><text x="30" y="80" fill="#ffb020" font-size="8">地面</text>
            <path d="M0 96 L200 96" stroke="#2d8cf0" stroke-width="1.2" /><text x="4" y="106" fill="#2d8cf0" font-size="8">海平面</text>
          </svg>
          <div class="wl-route__stepper">
            <button type="button" @click="stepGlobalHeight(100)">+100</button>
            <button type="button" @click="stepGlobalHeight(10)">+10</button>
            <a-input-number size="small" :value="draft.waypoint.globalHeight" :min="heightMin" :max="1500" :precision="1" :controls="false" @change="setGlobalHeight" /><span class="unit">m</span>
            <button type="button" @click="stepGlobalHeight(-10)">-10</button>
            <button type="button" @click="stepGlobalHeight(-100)">-100</button>
          </div>
        </div>
        <p class="wl-route__hint">{{ heightMode === 'WGS84' ? '编辑器按 WGS84 椭球高编辑，保存时写入 EGM96 海拔高。' : '相对参考起飞点的高度；切换模式时航点位置保持不变。' }}</p>
      </section>

      <section class="wl-route__card">
        <h4>全局航线速度</h4>
        <div class="wl-route__speed">
          <button type="button" :disabled="draft.waypoint.autoFlightSpeed <= 1" @click="stepSpeed(-1)"><MinusOutlined /></button>
          <span><b>{{ draft.waypoint.autoFlightSpeed }}</b> m/s</span>
          <button type="button" :disabled="draft.waypoint.autoFlightSpeed >= maxSpeed" @click="stepSpeed(1)"><PlusOutlined /></button>
        </div>
      </section>
    </template>

    <template v-else>
      <section class="wl-route__card">
        <h4>测绘参数</h4>
        <div class="wl-route__row"><span>采集镜头</span>
          <a-select size="small" v-model:value="draft.mapping.lens" dropdown-class-name="wl-select" @change="onLensChange">
            <a-select-option v-for="lens in editor.payloadProfile.value?.lenses || []" :key="lens" :value="lens">{{ LENS_LABELS[lens] }}</a-select-option>
          </a-select>
        </div>
        <div class="wl-route__row"><span>高度模式</span>
          <a-select size="small" :value="draft.mapping.heightMode" dropdown-class-name="wl-select" @change="editor.setHeightMode">
            <a-select-option value="relativeToStartPoint">相对起飞点高度 (ALT)</a-select-option>
            <a-select-option value="WGS84">海拔高度 (ASL)</a-select-option>
          </a-select>
        </div>
        <div class="wl-route__row"><span>航线高度 (m)</span><a-input-number size="small" :value="draft.mapping.height" :min="heightMin" :max="1500" :precision="1" @change="setMappingHeight" /></div>
        <div class="wl-route__row"><span>GSD (cm/px)</span><a-input-number size="small" :value="draft.mapping.gsd" :min="0.1" :max="100" :precision="2" :step="0.1" @change="setGsd" /></div>
        <div class="wl-route__row"><span>飞行速度 (m/s)</span><a-input-number size="small" v-model:value="draft.mapping.autoFlightSpeed" :min="1" :max="maxSpeed" :precision="1" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>航线角度 (°)</span><a-slider :value="draft.mapping.direction" :min="0" :max="359" style="flex: 1" @change="setDirection" /><a-input-number size="small" :value="draft.mapping.direction" :min="0" :max="359" @change="setDirection" /></div>
        <div class="wl-route__row"><span>高程优化</span><a-switch size="small" v-model:checked="draft.mapping.elevationOptimizeEnable" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>航向重叠率 (%)</span><a-input-number size="small" v-model:value="draft.mapping.orthoCameraOverlapH" :min="10" :max="90" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>旁向重叠率 (%)</span><a-input-number size="small" v-model:value="draft.mapping.orthoCameraOverlapW" :min="10" :max="90" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>边距 (m)</span><a-input-number size="small" v-model:value="draft.mapping.margin" :min="0" :max="500" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>拍照模式</span>
          <a-radio-group size="small" v-model:value="draft.mapping.shootType" @change="editor.commit">
            <a-radio-button value="time">等时拍照</a-radio-button>
            <a-radio-button value="distance">等距拍照</a-radio-button>
          </a-radio-group>
        </div>
        <div class="wl-route__row"><span>自定义相机角度</span><a-switch size="small" :checked="draft.mapping.gimbalPitchMode === 'fixed'" @change="setCustomPitch" /></div>
        <div class="wl-route__row" v-if="draft.mapping.gimbalPitchMode === 'fixed'"><span>云台俯仰 (°)</span><a-input-number size="small" v-model:value="draft.mapping.gimbalPitchAngle" :min="-90" :max="-30" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>机头朝向</span>
          <a-radio-group size="small" v-model:value="draft.mapping.mappingHeadingMode" @change="editor.commit">
            <a-radio-button value="followWayline">沿航线</a-radio-button>
            <a-radio-button value="fixed">固定角度</a-radio-button>
          </a-radio-group>
        </div>
        <div class="wl-route__row" v-if="draft.mapping.mappingHeadingMode === 'fixed'"><span>固定偏航角 (°)</span><a-input-number size="small" v-model:value="draft.mapping.mappingHeadingAngle" :min="0" :max="360" @change="editor.commit" /></div>
        <div class="wl-route__row"><span>航线起点</span>
          <a-select size="small" v-model:value="draft.mapping.startVertexIndex" dropdown-class-name="wl-select" @change="editor.commit">
            <a-select-option v-for="(vertex, index) in draft.mapping.vertices" :key="vertex.id" :value="index">顶点 {{ index + 1 }} 附近</a-select-option>
          </a-select>
        </div>
        <div class="wl-route__row"><span>照片存储镜头</span><a-checkbox-group :value="draft.mapping.imageFormat" :options="lensOptions" @change="setMappingImageFormat" /></div>
      </section>
      <section class="wl-route__card">
        <h4>安全起飞高 (ALT)</h4>
        <div class="wl-route__speed">
          <button type="button" @click="stepSafety(-10)"><MinusOutlined /></button>
          <span><b>{{ draft.missionConfig.takeOffSecurityHeight }}</b> m</span>
          <button type="button" @click="stepSafety(10)"><PlusOutlined /></button>
        </div>
      </section>
    </template>

    <section class="wl-route__card wl-route__advanced">
      <button type="button" class="wl-route__advanced-toggle" @click="advancedOpen = !advancedOpen">高级设置 <UpOutlined v-if="advancedOpen" /><DownOutlined v-else /></button>
      <div v-show="advancedOpen" class="wl-route__advanced-body">
        <div class="wl-route__field">
          <label>起飞速度</label>
          <div class="wl-route__speed compact">
            <button type="button" :disabled="draft.missionConfig.globalTransitionalSpeed <= 1" @click="set(() => { draft.missionConfig.globalTransitionalSpeed = Math.max(1, draft.missionConfig.globalTransitionalSpeed - 1) })"><MinusOutlined /></button>
            <span><b>{{ draft.missionConfig.globalTransitionalSpeed }}</b> m/s</span>
            <button type="button" :disabled="draft.missionConfig.globalTransitionalSpeed >= 15" @click="set(() => { draft.missionConfig.globalTransitionalSpeed = Math.min(15, draft.missionConfig.globalTransitionalSpeed + 1) })"><PlusOutlined /></button>
          </div>
        </div>
        <div class="wl-route__field"><label>返航高度 (m)</label><a-input-number size="small" v-model:value="draft.missionConfig.globalRTHHeight" :min="2" :max="1500" @change="editor.commit" /></div>
        <template v-if="draft.templateType === 'waypoint'">
          <div class="wl-route__field">
            <label>航点类型</label>
            <a-select size="small" v-model:value="draft.waypoint.globalWaypointTurnMode" dropdown-class-name="wl-select" @change="editor.commit">
              <a-select-option v-for="option in TURN_MODE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</a-select-option>
            </a-select>
          </div>
          <div v-if="isCurve" class="wl-route__field"><label>航段尽量直线</label><a-switch size="small" v-model:checked="draft.waypoint.globalUseStraightLine" @change="editor.commit" /></div>
          <div class="wl-route__field">
            <label>飞行器偏航角模式 <a-tooltip title="新增航点默认沿用此模式；从虚拟飞行器新增并记录姿态的航点使用自定义偏航角。"><InfoCircleOutlined /></a-tooltip></label>
            <a-select size="small" v-model:value="draft.waypoint.globalHeadingParam.waypointHeadingMode" dropdown-class-name="wl-select" @change="editor.commit">
              <a-select-option v-for="option in HEADING_MODE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</a-select-option>
            </a-select>
          </div>
          <div v-if="draft.waypoint.globalHeadingParam.waypointHeadingMode === 'smoothTransition'" class="wl-route__field"><label>目标偏航角 (°)</label><a-input-number size="small" v-model:value="draft.waypoint.globalHeadingParam.waypointHeadingAngle" :min="-180" :max="180" @change="editor.commit" /></div>
          <div v-if="draft.waypoint.globalHeadingParam.waypointHeadingMode === 'towardPOI'" class="wl-route__field">
            <label>全局兴趣点</label>
            <span class="value">{{ draft.waypoint.globalHeadingParam.waypointPoiPoint ? `${draft.waypoint.globalHeadingParam.waypointPoiPoint[1].toFixed(5)}, ${draft.waypoint.globalHeadingParam.waypointPoiPoint[0].toFixed(5)}` : '未设置' }}</span>
            <a-button size="small" @click="editor.pickPoi('global')">在地图上设置</a-button>
          </div>
          <div class="wl-route__field">
            <label>偏航旋转方向</label>
            <a-select size="small" v-model:value="draft.waypoint.globalHeadingParam.waypointHeadingPathMode" dropdown-class-name="wl-select" @change="editor.commit">
              <a-select-option value="followBadArc">最短路径</a-select-option>
              <a-select-option value="clockwise">顺时针</a-select-option>
              <a-select-option value="counterClockwise">逆时针</a-select-option>
            </a-select>
          </div>
          <div class="wl-route__field">
            <label>航点间云台俯仰角控制模式 <a-tooltip title="“依照每个航点设置”时，云台在航点间平滑转到下一航点记录的俯仰角。"><InfoCircleOutlined /></a-tooltip></label>
            <a-select size="small" v-model:value="draft.waypoint.gimbalPitchMode" dropdown-class-name="wl-select" @change="editor.commit">
              <a-select-option value="usePointSetting">依照每个航点设置</a-select-option>
              <a-select-option value="manual">手动控制</a-select-option>
            </a-select>
          </div>
        </template>
        <div class="wl-route__field">
          <label>完成动作 <a-tooltip title="航线执行完成后飞行器的动作。"><InfoCircleOutlined /></a-tooltip></label>
          <a-select size="small" v-model:value="draft.missionConfig.finishAction" dropdown-class-name="wl-select" @change="editor.commit">
            <a-select-option value="goHome">自动返航</a-select-option>
            <a-select-option value="noAction">退出航线模式</a-select-option>
            <a-select-option value="autoLand">原地降落</a-select-option>
            <a-select-option value="gotoFirstWaypoint">返回首航点</a-select-option>
          </a-select>
        </div>
        <div class="wl-route__field">
          <label>失控时</label>
          <a-select size="small" v-model:value="draft.missionConfig.exitOnRCLost" dropdown-class-name="wl-select" @change="editor.commit">
            <a-select-option value="executeLostAction">退出航线并执行失控动作</a-select-option>
            <a-select-option value="goContinue">继续执行航线</a-select-option>
          </a-select>
        </div>
        <div v-if="draft.missionConfig.exitOnRCLost === 'executeLostAction'" class="wl-route__field">
          <label>失控动作</label>
          <a-select size="small" v-model:value="draft.missionConfig.executeRCLostAction" dropdown-class-name="wl-select" @change="editor.commit">
            <a-select-option value="goBack">返航</a-select-option>
            <a-select-option value="hover">悬停</a-select-option>
            <a-select-option value="landing">降落</a-select-option>
          </a-select>
        </div>
        <div v-if="draft.templateType === 'waypoint'" class="wl-route__field"><label>照片存储镜头</label><a-checkbox-group :value="draft.waypoint.imageFormat" :options="lensOptions" @change="setWaypointImageFormat" /></div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { CheckCircleFilled, DownOutlined, EnvironmentOutlined, ExclamationCircleFilled, InfoCircleOutlined, MinusOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons-vue'
import type { PayloadLens } from '/@/types/wayline-editor'
import { HEADING_MODE_OPTIONS, LENS_LABELS, TURN_MODE_OPTIONS, cameraSpec, findPayloadProfile, gsdToHeight, heightToGsd, round } from '/@/utils/wayline-editor'
import { cesiumSceneApi } from '/@/store/cesium-map'
import { useWaylineEditor } from './editor-context'

const editor = useWaylineEditor()
const draft = editor.draft
const advancedOpen = ref(false)
const takeoff = computed(() => draft.missionConfig.takeOffRefPoint)
const heightMode = computed(() => editor.heightMode.value)
const heightMin = computed(() => heightMode.value === 'WGS84' ? -500 : 2)
const maxSpeed = computed(() => editor.droneProfile.value?.maxFlightSpeed || 15)
const hasIr = computed(() => Boolean(editor.payloadProfile.value?.lenses.includes('ir')))
const lensOptions = computed(() => (editor.payloadProfile.value?.lenses || ['wide']).map(lens => ({ label: LENS_LABELS[lens], value: lens })))
const isCurve = computed(() => draft.waypoint.globalWaypointTurnMode === 'toPointAndStopWithContinuityCurvature' || draft.waypoint.globalWaypointTurnMode === 'toPointAndPassWithContinuityCurvature')
const photoMode = computed(() => {
  const format = draft.waypoint.imageFormat
  const ir = format.includes('ir')
  const visible = format.some(lens => lens !== 'ir')
  return ir && visible ? 'both' : ir ? 'ir' : 'visible'
})

function set (change: () => void) { editor.mutate(change) }

function setPhotoMode (mode: 'visible' | 'ir' | 'both') {
  const lenses = editor.payloadProfile.value?.lenses || ['wide']
  const visible = lenses.filter(lens => lens !== 'ir')
  set(() => {
    draft.waypoint.imageFormat = mode === 'ir' ? ['ir'] : mode === 'both' ? [...visible.slice(0, 1), 'ir'] : visible.slice(0, 1)
  })
}

function setPayload (key: string) {
  set(() => {
    draft.payloadModelKey = key
    const lenses = findPayloadProfile(draft.droneModelKey, key)?.lenses || ['wide']
    draft.waypoint.imageFormat = draft.waypoint.imageFormat.filter(lens => lenses.includes(lens))
    if (!draft.waypoint.imageFormat.length) draft.waypoint.imageFormat = [lenses[0]]
    if (!lenses.includes(draft.mapping.lens)) draft.mapping.lens = lenses[0]
    draft.mapping.imageFormat = draft.mapping.imageFormat.filter(lens => lenses.includes(lens))
    if (!draft.mapping.imageFormat.length) draft.mapping.imageFormat = [draft.mapping.lens]
    syncGsd()
  })
}

function stepSafety (delta: number) {
  set(() => { draft.missionConfig.takeOffSecurityHeight = Math.min(1500, Math.max(8, draft.missionConfig.takeOffSecurityHeight + delta)) })
}
function setSafety (value: number | null) {
  if (value === null) return
  set(() => { draft.missionConfig.takeOffSecurityHeight = Math.min(1500, Math.max(8, Math.round(value))) })
}
function stepGlobalHeight (delta: number) {
  set(() => { draft.waypoint.globalHeight = round(Math.min(1500, Math.max(heightMin.value, draft.waypoint.globalHeight + delta)), 1) })
}
function setGlobalHeight (value: number | null) {
  if (value === null) return
  set(() => { draft.waypoint.globalHeight = round(Math.min(1500, Math.max(heightMin.value, value)), 1) })
}
function stepSpeed (delta: number) {
  set(() => { draft.waypoint.autoFlightSpeed = Math.min(maxSpeed.value, Math.max(1, Math.round(draft.waypoint.autoFlightSpeed + delta))) })
}
function setWaypointImageFormat (values: PayloadLens[]) {
  if (!values.length) return
  set(() => { draft.waypoint.imageFormat = values })
}
function setMappingImageFormat (values: PayloadLens[]) {
  if (!values.length) return
  set(() => { draft.mapping.imageFormat = values })
}
function syncGsd () { draft.mapping.gsd = round(heightToGsd(draft.mapping.height, cameraSpec(draft)), 2) }
function onLensChange () { syncGsd(); editor.commit() }
function setMappingHeight (value: number | null) {
  if (value === null) return
  set(() => { draft.mapping.height = value; syncGsd() })
}
function setGsd (value: number | null) {
  if (value === null || value <= 0) return
  set(() => { draft.mapping.gsd = value; draft.mapping.height = round(gsdToHeight(value, cameraSpec(draft)), 1) })
}
function setDirection (value: number | null) { if (value !== null) set(() => { draft.mapping.direction = value }) }
function setCustomPitch (enabled: boolean) { set(() => { draft.mapping.gimbalPitchMode = enabled ? 'fixed' : 'manual' }) }

function setAglHeight (value: number | null) {
  if (value === null || !Number.isFinite(value) || !draft.missionConfig.takeOffRefPoint) return
  set(() => { draft.missionConfig.takeOffRefPoint!.aglHeight = value })
}

function useAircraftPosition () {
  const aircraft = editor.aircraft
  const sampled = cesiumSceneApi.value?.groundHeight(aircraft.longitude, aircraft.latitude)
  // Map samples are in the drawing frame; the draft keeps ellipsoid heights.
  const ground = sampled === null || sampled === undefined ? (draft.missionConfig.takeOffRefPoint?.ellipsoidHeight ?? 0) : sampled + editor.sceneOffset.value
  set(() => { draft.missionConfig.takeOffRefPoint = { longitude: aircraft.longitude, latitude: aircraft.latitude, ellipsoidHeight: ground, aglHeight: 0 }; draft.dock = null })
  editor.refinePadHeight()
}
</script>

<style lang="scss">
.wl-route {
  width: 320px; max-height: calc(100vh - 60px); overflow: auto; padding: 8px; display: flex; flex-direction: column; gap: 8px; color: var(--wl-text, #e9eaec); font-size: 12px;
  --wl-border: #35373c; --wl-muted: #9a9ea6; --wl-accent: #2d8cf0;
  h4 { margin: 0 0 8px; color: #fff; font-size: 12px; font-weight: 600; }
  &__card { padding: 10px 12px; border-radius: 6px; background: #24262a; }
  &__takeoff { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 6px; }
  &__takeoff-status { display: flex; align-items: center; gap: 6px; color: #fff; }
  &__takeoff-status .ok { color: #19be6b; }
  &__takeoff-status .warn { color: #ff9f1a; }
  &__link { border: 0; background: transparent; color: var(--wl-accent); cursor: pointer; padding: 0; white-space: nowrap; }
  &__link:hover { text-decoration: underline; }
  &__coords { grid-column: 1 / -1; margin: 0; color: var(--wl-muted); font-variant-numeric: tabular-nums; }
  &__gap { grid-column: 1 / -1; margin: 4px 0 0; color: #f2d299; line-height: 1.5; }
  &__agl { grid-column: 1 / -1; display: flex; align-items: center; gap: 8px; margin-top: 4px; color: #c5cbd2; }
  &__agl .ant-input-number { width: 90px; }
  &__agl .unit { color: var(--wl-muted); }
  &__segmented { display: flex; border-radius: 4px; overflow: hidden; background: #101113; margin-bottom: 8px; }
  &__segmented button { flex: 1; height: 30px; border: 0; background: transparent; color: #d5d8de; cursor: pointer; white-space: nowrap; }
  &__segmented button.active { background: var(--wl-accent); color: #fff; }
  &__segmented button:disabled { color: #5c6068; cursor: not-allowed; }
  &__figure { display: grid; grid-template-columns: 1fr 74px; gap: 8px; align-items: center; }
  &__diagram { width: 100%; height: auto; }
  &__stepper { display: flex; flex-direction: column; align-items: stretch; gap: 3px; }
  &__stepper button { height: 22px; border: 0; border-radius: 3px; background: #35373c; color: #fff; cursor: pointer; font-size: 11px; }
  &__stepper button:hover { background: #4a4d55; }
  &__stepper .ant-input-number { width: 100%; }
  &__stepper .ant-input-number-input { text-align: center; color: var(--wl-accent) !important; font-size: 15px; font-weight: 600; height: 28px; }
  &__stepper .unit { text-align: center; color: var(--wl-muted); line-height: 1; margin-top: -2px; }
  &__speed { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  &__speed span { flex: 1; text-align: center; color: var(--wl-muted); }
  &__speed b { color: var(--wl-accent); font-size: 18px; font-weight: 600; margin-right: 4px; }
  &__speed button { width: 30px; height: 28px; border: 0; border-radius: 3px; background: #35373c; color: #fff; cursor: pointer; }
  &__speed button:disabled { color: #5c6068; cursor: not-allowed; }
  &__speed.compact b { font-size: 14px; }
  &__hint { margin: 8px 0 0; color: var(--wl-muted); line-height: 1.5; }
  &__row { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-bottom: 6px; }
  &__row > span:first-child { min-width: 96px; color: #c5cbd2; }
  &__row .ant-select { min-width: 140px; }
  &__row .ant-input-number { width: 100px; }
  &__advanced-toggle { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; border: 0; background: transparent; color: #fff; font-size: 13px; cursor: pointer; padding: 2px 0; }
  &__advanced-body { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--wl-border); display: flex; flex-direction: column; gap: 10px; }
  &__field { display: flex; flex-direction: column; gap: 4px; }
  &__field label { color: #c5cbd2; display: flex; align-items: center; gap: 4px; }
  &__field .value { color: #fff; }
  &__field .ant-select, &__field .ant-input-number { width: 100%; }
}
</style>
